/**
 * SearchableDropdown.jsx
 *
 * Typeahead dropdown — Sandvik TypeAhead pattern, no rounded corners.
 * Standalone UI primitive consumed by the Tool Finder Replace path:
 *   - D7 StepEquipmentSelect (manufacturer + model cascade for both
 *     the Rig tab and the Rock Drill tab).
 *
 * PORTED VERBATIM from TH ToolFinder v4 — inline SearchableDropdown
 * in `tophammer-toolfinder-v4/src/components/wizard/StepEquipmentSelect.jsx`
 * lines 38-199. Extracted here as a standalone primitive so D7 and any
 * future searchable picker can re-use it.
 *
 * SP3.A.2-enhance (2026-05-18): richer typeahead capabilities ported
 * from the Resharpening VC `BitModal` search mode
 * (`resharpening-vc-round3-m6/src/pages/Step3BitSelection.jsx` lines
 * 428-518). All additions are opt-in props with safe defaults — the
 * original 11 tests pass unchanged.
 *
 * Enhancements added (each opt-in, default behaviour unchanged):
 *   - `primaryField` / `secondaryField`  → two-line result rows.
 *   - `renderItem(item)`                 → full custom row renderer.
 *   - `renderBadges(item)`               → right-side meta/badges.
 *   - `loading`                          → swap chevron for spinner.
 *   - `minChars`                         → gate filter until threshold.
 *   - `debounceMs`                       → debounced filter rendering.
 *   - `favoritesSlot`                    → ReactNode above results.
 *   - Result count strip ("Type N+...", "Searching...", "X results").
 *   - `searchKeywords` field on items is searched automatically when
 *     present (no new prop; mirrors TF v4 `useSearch`).
 *
 * Behaviour contract (preserved from TF v4):
 *   - Local state: `query`, `isOpen`, `highlighted`.
 *   - `useMemo` filtered list — case-insensitive substring match of
 *     `query` against each of `searchFields` (default: `[displayField]`).
 *   - `highlighted` resets to 0 whenever the filtered list length changes.
 *   - Click outside the container (mousedown) closes the dropdown.
 *   - Syncs `query` to '' when the external `value` becomes null/falsy.
 *   - Keyboard nav:
 *       Closed:  ArrowDown / Enter  → opens.
 *       Open:    ArrowDown          → highlight next (clamped at last).
 *                ArrowUp            → highlight previous (clamped at 0).
 *                Enter              → select highlighted item.
 *                Escape             → close (no selection).
 *   - Selecting an item sets `query` to `item[displayField]` and closes.
 *   - Typing while a value is selected clears the selection
 *     (calls `onChange(null)`).
 *
 * Adaptations / icon choice:
 *   - `lucide-react@0.542.0` is already a project dep (package.json).
 *     We use `Search`, `Loader2`, `ChevronDown`, `ChevronUp` from it.
 *     This is the first consumer of lucide-react in MRT src.
 *
 * @module components/ui/SearchableDropdown
 * @since 2026-05-18 (Group D Sub-Plan 3, Task SP3.A.2 + .2-enhance)
 */

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Search, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { useDebounce } from '@hooks/useDebounce';

/**
 * SearchableDropdown — typeahead picker.
 *
 * @param {Object} props
 * @param {string} [props.label]
 * @param {string} [props.placeholder]    default: 'Select...'
 * @param {Array<Object>} props.items
 * @param {Object|null} props.value       currently selected item (or null)
 * @param {(item: Object|null) => void} props.onChange
 * @param {boolean} [props.disabled]
 * @param {string} [props.displayField]   default: 'label'
 * @param {string[]} [props.searchFields] default: [displayField]
 * @param {string} [props.primaryField]   default: displayField — bold primary line
 * @param {string} [props.secondaryField] optional muted second line
 * @param {(item: Object) => React.ReactNode} [props.renderItem]
 *        Full row override (replaces primary/secondary entirely).
 * @param {(item: Object) => React.ReactNode} [props.renderBadges]
 *        Right-side meta/badges renderer (used unless renderItem is set).
 * @param {boolean} [props.loading]       show spinner instead of chevron
 * @param {number} [props.minChars]       default: 0 — gate filter until met
 * @param {number} [props.debounceMs]     default: 0 — debounce filter
 * @param {React.ReactNode} [props.favoritesSlot] rendered above results
 */
const SearchableDropdown = ({
  label,
  placeholder = 'Select...',
  items = [],
  value,
  onChange,
  disabled = false,
  displayField = 'label',
  searchFields,
  primaryField,
  secondaryField,
  renderItem,
  renderBadges,
  loading = false,
  minChars = 0,
  debounceMs = 0,
  favoritesSlot = null,
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const fields = searchFields || [displayField];
  const effectivePrimary = primaryField || displayField;

  // Debounce the query when debounceMs > 0. With debounceMs === 0 the hook
  // returns the value on the next tick (useEffect → setState), which would
  // surprise existing call sites; so we short-circuit to the raw query.
  const debouncedQuery = useDebounce(query, debounceMs);
  const effectiveQuery = debounceMs > 0 ? debouncedQuery : query;

  const filtered = useMemo(() => {
    const trimmed = effectiveQuery.trim();
    // minChars gate — when below threshold, show no filtered list.
    // Backward-compat: when minChars === 0 and query is empty, show all items.
    if (minChars > 0 && trimmed.length < minChars) return [];
    if (!trimmed) return items;
    const term = trimmed.toLowerCase();
    return items.filter((item) => {
      // searchFields match
      if (fields.some((f) => String(item[f] || '').toLowerCase().includes(term))) {
        return true;
      }
      // searchKeywords array match (TF v4 useSearch parity, no new prop)
      if (Array.isArray(item.searchKeywords)) {
        if (item.searchKeywords.some((kw) => String(kw || '').toLowerCase().includes(term))) {
          return true;
        }
      }
      return false;
    });
  }, [effectiveQuery, items, fields, minChars]);

  // Reset highlighted when filtered list changes.
  useEffect(() => {
    setHighlighted(0);
  }, [filtered.length]);

  // Click outside → close.
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // When value clears externally, clear the visible query.
  useEffect(() => {
    if (!value) setQuery('');
  }, [value]);

  const handleSelect = useCallback(
    (item) => {
      onChange(item);
      setQuery(item[displayField]);
      setIsOpen(false);
    },
    [onChange, displayField],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen) {
        if (e.key === 'ArrowDown' || e.key === 'Enter') {
          setIsOpen(true);
          e.preventDefault();
        }
        return;
      }
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlighted((prev) => Math.min(prev + 1, filtered.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlighted((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (filtered[highlighted]) handleSelect(filtered[highlighted]);
          break;
        case 'Escape':
          setIsOpen(false);
          break;
        default:
          break;
      }
    },
    [isOpen, filtered, highlighted, handleSelect],
  );

  const handleInputChange = useCallback(
    (e) => {
      setQuery(e.target.value);
      setIsOpen(true);
      // Clear selection if user types after selecting.
      if (value) onChange(null);
    },
    [value, onChange],
  );

  // Determine whether to show the result-count strip / favoritesSlot.
  // We show the result strip whenever the dropdown is open AND either:
  //   - minChars > 0 (so the user needs guidance), OR
  //   - loading is true (so user sees "Searching..."), OR
  //   - filtered.length === 0 with a non-empty query (consolidated empty).
  const trimmedQuery = effectiveQuery.trim();
  const belowMinChars = minChars > 0 && trimmedQuery.length < minChars;
  const showResultStrip = isOpen && (minChars > 0 || loading);
  const resultStripText = loading
    ? 'Searching...'
    : belowMinChars
      ? `Type ${minChars}+ characters to search`
      : `${filtered.length} results`;

  // Right-end input adornment: spinner when loading, chevron toggle otherwise.
  const renderRightAdornment = () => {
    if (loading) {
      return (
        <span
          aria-label="Loading"
          role="status"
          className="absolute right-3 top-1/2 -translate-y-1/2 leading-none text-electric-blue"
        >
          <Loader2 size={18} className="animate-spin" aria-hidden="true" />
        </span>
      );
    }
    return (
      <button
        type="button"
        tabIndex={-1}
        aria-label={isOpen ? 'Close dropdown' : 'Open dropdown'}
        onClick={() => {
          if (!disabled) {
            setIsOpen(!isOpen);
            inputRef.current?.focus();
          }
        }}
        className="absolute right-3 top-1/2 -translate-y-1/2 leading-none"
      >
        <span aria-hidden="true" className={disabled ? 'text-n3' : 'text-n6'}>
          {isOpen ? (
            <ChevronUp size={18} aria-hidden="true" />
          ) : (
            <ChevronDown size={18} aria-hidden="true" />
          )}
        </span>
      </button>
    );
  };

  // Per-row content: renderItem > primary/secondary + renderBadges.
  const renderRow = (item) => {
    if (typeof renderItem === 'function') {
      return renderItem(item);
    }
    const primaryText = item[effectivePrimary];
    const secondaryText = secondaryField ? item[secondaryField] : null;
    return (
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-normal text-sm truncate">{primaryText}</div>
          {secondaryText ? (
            <div className="text-xs text-n5 truncate">{secondaryText}</div>
          ) : null}
        </div>
        {typeof renderBadges === 'function' ? (
          <div className="flex items-center gap-2 flex-shrink-0">
            {renderBadges(item)}
          </div>
        ) : null}
      </div>
    );
  };

  // Show the dropdown panel when open AND we have anything to show:
  // either favoritesSlot, the result strip, results, or the empty-state.
  const hasEmptyState = isOpen && filtered.length === 0 && trimmedQuery && !belowMinChars;
  const showPanel =
    isOpen && (favoritesSlot || showResultStrip || filtered.length > 0 || hasEmptyState);

  return (
    <div className="space-y-1" ref={containerRef}>
      {label && (
        <label className="block text-sm font-sandvik-text font-normal text-carbon-black">
          {label}
        </label>
      )}
      <div className="relative">
        {/* Left search icon — always present, matches Resharpening VC. */}
        <span
          aria-hidden="true"
          className="absolute left-3 top-1/2 -translate-y-1/2 leading-none text-n4 pointer-events-none"
        >
          <Search size={18} aria-hidden="true" />
        </span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => !disabled && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={[
            'w-full pl-10 pr-10 py-3 border-2 rounded-none',
            'font-sandvik-text text-carbon-black placeholder-n4',
            'focus:outline-none focus:border-electric-blue transition-colors',
            disabled
              ? 'bg-n1 border-n3 cursor-not-allowed text-n4'
              : 'bg-white border-n4',
          ].join(' ')}
        />
        {renderRightAdornment()}

        {showPanel && (
          <div
            className="absolute z-50 w-full mt-0 bg-white border-2 border-electric-blue border-t-0 shadow-lg"
          >
            {favoritesSlot ? (
              <div data-testid="favorites-slot">{favoritesSlot}</div>
            ) : null}

            {showResultStrip ? (
              <div
                data-testid="result-count"
                className="px-4 py-2 text-xs text-n6 font-sandvik-text border-b border-n2"
              >
                {resultStripText}
              </div>
            ) : null}

            {filtered.length > 0 ? (
              <ul role="listbox" className="max-h-64 overflow-y-auto">
                {filtered.map((item, index) => {
                  const isHighlighted = index === highlighted;
                  const isSelected = item === value;
                  return (
                    <li
                      key={item.id || item[displayField] || index}
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => handleSelect(item)}
                      className={[
                        'px-4 py-3 cursor-pointer transition-colors border-b border-n2 last:border-b-0',
                        'font-sandvik-text text-sm',
                        isHighlighted ? 'bg-eb-1' : 'hover:bg-n1',
                        isSelected ? 'font-normal text-electric-blue' : 'text-carbon-black',
                      ].join(' ')}
                    >
                      {renderRow(item)}
                    </li>
                  );
                })}
              </ul>
            ) : null}

            {hasEmptyState ? (
              <div
                role="status"
                className="px-4 py-3 font-sandvik-text text-sm text-n4"
              >
                No matches found
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchableDropdown;
